class KmpFilterGrid extends HTMLElement {
  connectedCallback() {
    this.buttons = [...this.querySelectorAll('[data-kmp-filter]')];
    this.items = [...this.querySelectorAll('[data-kmp-filter-values]')];

    this.buttons.forEach((button) => {
      button.addEventListener('click', () => this.applyFilter(button.dataset.kmpFilter));
    });
  }

  applyFilter(value) {
    this.buttons.forEach((button) => {
      const active = button.dataset.kmpFilter === value;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
    });

    this.items.forEach((item) => {
      const values = (item.dataset.kmpFilterValues || '').split(/\s+/);
      item.hidden = value !== 'all' && !values.includes(value);
    });
  }
}

if (!customElements.get('kmp-filter-grid')) {
  customElements.define('kmp-filter-grid', KmpFilterGrid);
}

class KmpMatchForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.steps = [...this.querySelectorAll('[data-kmp-match-step]')];
    this.progress = this.querySelector('.kmp-match__progress');

    if (!this.form || this.steps.length === 0 || !this.progress) return;

    this.currentStep = 0;
    this.setAttribute('data-enhanced', '');

    this.querySelectorAll('[data-kmp-match-next]').forEach((button) => {
      button.addEventListener('click', () => this.goForward());
    });

    this.querySelectorAll('[data-kmp-match-back]').forEach((button) => {
      button.addEventListener('click', () => this.showStep(this.currentStep - 1));
    });

    this.showStep(0, false);
  }

  goForward() {
    const step = this.steps[this.currentStep];
    const invalidField = step.querySelector(':invalid');

    if (invalidField) {
      invalidField.reportValidity();
      return;
    }

    this.showStep(this.currentStep + 1);
  }

  showStep(nextStep, moveFocus = true) {
    this.currentStep = Math.max(0, Math.min(nextStep, this.steps.length - 1));

    this.steps.forEach((step, index) => {
      const active = index === this.currentStep;
      step.classList.toggle('is-active', active);
      step.toggleAttribute('inert', !active);
    });

    const progressValue = this.currentStep + 1;
    this.progress.setAttribute('aria-valuenow', String(progressValue));
    this.progress.querySelector('span').style.width = `${(progressValue / this.steps.length) * 100}%`;

    if (moveFocus) {
      const heading = this.steps[this.currentStep].querySelector('legend');
      if (heading) {
        heading.tabIndex = -1;
        heading.focus();
      }
    }
  }
}

if (!customElements.get('kmp-match-form')) {
  customElements.define('kmp-match-form', KmpMatchForm);
}

class KmpHeader extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector('[data-kmp-header-toggle]');
    this.mobileNav = this.querySelector('[data-kmp-header-mobile]');
    this.localization = this.querySelector('[data-kmp-header-localization]');
    this.headerInner = this.querySelector('.kmp-header__inner');
    this.pageWrapper = this.closest('.page-wrapper');
    this.headerGroup = this.closest('#header-group');

    if (!this.toggle || !this.mobileNav) return;

    this.abortController = new AbortController();
    const { signal } = this.abortController;
    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => this.syncHeaderHeights());
      if (this.headerInner) this.resizeObserver.observe(this.headerInner);
      if (this.headerGroup) this.resizeObserver.observe(this.headerGroup);
    }

    this.toggle.addEventListener('click', () => this.setOpen(!this.classList.contains('is-open')), { signal });
    this.mobileNav.addEventListener(
      'click',
      (event) => {
        if (event.target.closest('a')) this.setOpen(false);
      },
      { signal },
    );
    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'Escape') return;

        const menuWasOpen = this.classList.contains('is-open');
        const localizationWasOpen = Boolean(this.localization?.open);
        if (!menuWasOpen && !localizationWasOpen) return;

        this.setOpen(false);
        this.localization?.removeAttribute('open');

        if (menuWasOpen && window.matchMedia('(max-width: 989px)').matches) {
          this.toggle.focus();
        } else if (localizationWasOpen) {
          this.localization.querySelector('summary')?.focus();
        }
      },
      { signal },
    );
    document.addEventListener(
      'click',
      (event) => {
        if (this.localization?.open && !this.localization.contains(event.target)) {
          this.localization.removeAttribute('open');
        }
      },
      { signal },
    );
    window.addEventListener('scroll', () => this.updateScrollState(), { signal, passive: true });
    this.pageWrapper?.addEventListener('scroll', () => this.updateScrollState(), { signal, passive: true });
    window.addEventListener(
      'resize',
      () => {
        if (window.matchMedia('(min-width: 990px)').matches) this.setOpen(false);
      },
      { signal },
    );

    this.syncHeaderHeights();
    this.updateScrollState();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.resizeObserver?.disconnect();
  }

  setOpen(open) {
    this.classList.toggle('is-open', open);
    this.toggle.setAttribute('aria-expanded', String(open));
    this.toggle.setAttribute(
      'aria-label',
      open ? this.toggle.dataset.closeLabel || this.toggle.getAttribute('aria-label') : this.toggle.dataset.openLabel || this.toggle.getAttribute('aria-label'),
    );
    this.mobileNav.hidden = !open;
  }

  updateScrollState() {
    const scrollTop = window.matchMedia('(min-width: 990px)').matches
      ? this.pageWrapper?.scrollTop || 0
      : window.scrollY;
    this.classList.toggle('is-scrolled', scrollTop > 8);
  }

  syncHeaderHeights() {
    const headerHeight = this.headerInner?.offsetHeight || 76;
    const headerGroupHeight = this.headerGroup
      ? [...this.headerGroup.children].reduce(
          (total, child) => total + (child.contains(this) ? headerHeight : child.offsetHeight),
          0,
        )
      : headerHeight;
    document.body.style.setProperty('--header-height', `${headerHeight}px`);
    document.body.style.setProperty('--header-group-height', `${headerGroupHeight}px`);
  }
}

if (!customElements.get('kmp-header')) {
  customElements.define('kmp-header', KmpHeader);
}

const kmpRevealSelector = [
  '.kmp-hero__visual',
  '.kmp-section-head',
  '.kmp-card-grid > *',
  '.kmp-coach-grid > *',
  '.kmp-product-grid > *',
  '.kmp-blog-grid > *',
  '.kmp-bento > *',
  '.kmp-faq details',
  '.kmp-cta-band',
  '.kmp-match',
].join(',');

const kmpReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const kmpRevealObserver =
  'IntersectionObserver' in window && !kmpReducedMotion.matches
    ? new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -8%', threshold: 0.08 },
      )
    : null;

function initKmpReveals(root = document) {
  const targets = [...root.querySelectorAll(kmpRevealSelector)].filter((target) => !target.dataset.kmpRevealReady);

  targets.forEach((target) => {
    target.dataset.kmpRevealReady = 'true';
    target.classList.add('kmp-reveal');

    const siblings = [...target.parentElement.children].filter((element) => element.matches(kmpRevealSelector));
    const siblingIndex = siblings.indexOf(target);
    target.style.setProperty('--kmp-reveal-delay', `${Math.min(Math.max(siblingIndex, 0), 3) * 80}ms`);

    if (kmpRevealObserver) {
      kmpRevealObserver.observe(target);
    } else {
      target.classList.add('is-visible');
    }
  });
}

initKmpReveals();
document.addEventListener('shopify:section:load', (event) => initKmpReveals(event.target));
